import type { AccessControlProvider, AuthProvider, DataProvider } from "@refinedev/core";
import { apiFetch, setCsrfToken } from "@contractor-quoting/api-client";

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const result = await apiFetch<{ csrfToken: string }>("/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      setCsrfToken(result.csrfToken);
      return { success: true, redirectTo: "/contractor/projects" };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: error instanceof Error ? error.message : "Login failed",
        },
      };
    }
  },
  logout: async () => {
    try {
      await apiFetch("/v1/auth/logout", { method: "POST" });
    } catch {
      /* local logout */
    }
    return { success: true, redirectTo: "/login" };
  },
  check: async () => {
    try {
      await apiFetch("/v1/auth/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, redirectTo: "/login" };
    }
  },
  getIdentity: async () => {
    try {
      return await apiFetch("/v1/auth/me");
    } catch {
      return null;
    }
  },
  onError: async (error) =>
    error?.statusCode === 401 ? { logout: true, redirectTo: "/login" } : { error },
};

export const accessControlProvider: AccessControlProvider = {
  can: async ({ action }) => {
    try {
      const me = await apiFetch<{ memberships: Array<{ role: "owner" | "staff" }> }>("/v1/auth/me");
      const ownerAction = ["create", "edit", "delete"].includes(action);
      return {
        can:
          me.memberships.length > 0 &&
          (!ownerAction || me.memberships.some((item) => item.role === "owner")),
      };
    } catch {
      return { can: false };
    }
  },
};

const pluralPath = (resource: string) => `/v1/contractor/${resource}`;
export const dataProvider: DataProvider = {
  getApiUrl: () => "/v1/contractor",
  getList: async ({ resource, filters }) => {
    const status = filters?.find(
      (item) => "field" in item && item.field === "status" && item.operator === "eq",
    );
    const query =
      status && "value" in status ? `?status=${encodeURIComponent(String(status.value))}` : "";
    const result = await apiFetch<{ items: Array<{ id: string }>; total: number }>(
      `${pluralPath(resource)}${query}`,
    );
    return { data: result.items as never, total: result.total };
  },
  getOne: async ({ resource, id }) => ({
    data: await apiFetch(`${pluralPath(resource)}/${String(id)}`),
  }),
  create: async ({ resource, variables }) => ({
    data: await apiFetch(pluralPath(resource), { method: "POST", body: JSON.stringify(variables) }),
  }),
  update: async ({ resource, id, variables }) => ({
    data: await apiFetch(`${pluralPath(resource)}/${String(id)}`, {
      method: "PATCH",
      body: JSON.stringify(variables),
    }),
  }),
  deleteOne: async ({ resource, id }) => ({
    data: await apiFetch(`${pluralPath(resource)}/${String(id)}`, { method: "DELETE" }),
  }),
};
